import React, { useEffect, useRef, useState } from 'react'
import styled from '@emotion/styled'
import { Helmet } from '@dr.pogodin/react-helmet'
import { Formik } from 'formik'
import * as Yup from 'yup'
import { useLocation, useNavigate } from 'react-router-dom'

import { Paper, Typography, TextField, Button, Alert as MuiAlert } from '@mui/material'

import { ReactComponent as Logo } from '../../vendor/logo.svg'

import UserApi from '../../api/UserApi'

const userApi = new UserApi()

const Brand = styled(Logo)`
    fill: ${(props) => props.theme.palette.primary.main};
    width: 64px;
    height: 64px;
    margin-bottom: 32px;
`

const Wrapper = styled(Paper)`
    padding: ${(props) => props.theme.spacing(6)};

    ${(props) => props.theme.breakpoints.up('md')} {
        padding: ${(props) => props.theme.spacing(10)};
    }
`

const Alert = styled(MuiAlert)`
    margin-bottom: ${(props) => props.theme.spacing(2)};
`

function useQuery() {
    return new URLSearchParams(useLocation().search)
}

function ResetPasswordWithCode() {
    const query = useQuery()
    const navigate = useNavigate()

    const emailRef = useRef(null)
    const codeRef = useRef(null)
    const newPasswordRef = useRef(null)
    const confirmPasswordRef = useRef(null)

    // State for showing message
    const [showMessage, setShowMessage] = useState(false)
    const [showForm, setShowForm] = useState(true)

    // Get email and code from query parameters
    const emailFromQuery = query.get('email') || ''
    const codeFromQuery = query.get('code') || ''

    useEffect(() => {
        if (query.get('showMessage') === 'true') {
            setShowMessage(true)
        }
    }, [query])

    // Focus on the first empty field
    useEffect(() => {
        if (!emailFromQuery) {
            emailRef.current.focus()
        } else if (!codeFromQuery) {
            codeRef.current.focus()
        } else {
            newPasswordRef.current.focus()
        }
    }, [emailFromQuery, codeFromQuery])

    const handleReRequestCode = async () => {
        try {
            // Trigger re-requesting the password reset code
            await userApi.requestPasswordResetCode(emailFromQuery)
            setShowMessage(true)
        } catch (error) {
            console.error('Error re-requesting code:', error)
        }
    }

    return (
        <React.Fragment>
            <Brand />
            <Wrapper>
                <Helmet title='Glömt lösenord' />

                <Typography component='h1' variant='h4' align='center' gutterBottom>
                    Återställ lösenord
                </Typography>

                {showMessage && (
                    <Alert severity='info'>
                        Om den angivna användaren finns hos oss så får du en återställningskod till din e-postadress.
                        Ange den här tillsammans med ditt nya lösenord.
                        <br />
                        <br />
                        Om du inte får någon e-post kan det bero på att du angivit fel e-postadress eller att din
                        användare är inaktiverad. Kontakta <a href='mailto:info@financelab.se'>info@financelab.se</a> om
                        du tror du tror att det kan vara fallet.
                    </Alert>
                )}

                {showForm ? (
                    <Formik
                        initialValues={{
                            email: emailFromQuery,
                            code: codeFromQuery,
                            newPassword: '',
                            confirmPassword: '',
                            submit: null,
                        }}
                        validationSchema={Yup.object().shape({
                            email: Yup.string()
                                .email('Måste vara en giltig epostadress')
                                .required('Epostadress måste anges'),
                            code: Yup.string().required('Återställningskod måste anges'),
                            newPassword: Yup.string()
                                .min(10, 'Lösenordet måste innehålla minst 10 tecken')
                                .required('Nytt lösenord måste anges'),
                            confirmPassword: Yup.string()
                                .oneOf([Yup.ref('newPassword'), null], 'Lösenorden matchar inte')
                                .required('Bekräfta lösenordet'),
                        })}
                        onSubmit={async (values, { setErrors, setStatus, setSubmitting }) => {
                            try {
                                await userApi.resetPasswordWithCode(values.email, values.code, values.newPassword)
                                setStatus({ success: true })
                                setSubmitting(false)
                                setShowForm(false) // Hide form after successful password reset
                            } catch (error) {
                                const errorMessage = error.message || 'Ogiltig kod eller fel vid återställning'
                                setStatus({ success: false })
                                setErrors({ submit: errorMessage })
                                setSubmitting(false)
                            }
                        }}
                    >
                        {({
                            errors,
                            handleBlur,
                            handleChange,
                            handleSubmit,
                            isSubmitting,
                            touched,
                            values,
                            status,
                        }) => (
                            <form noValidate onSubmit={handleSubmit}>
                                {status && status.success && (
                                    <Alert severity='success'>
                                        Lösenordet har återställts. Du kan nu{' '}
                                        <Button color='inherit' onClick={() => navigate('/auth/sign-in')}>
                                            logga in
                                        </Button>
                                        .
                                    </Alert>
                                )}
                                {errors.submit && (
                                    <Alert severity='error'>
                                        {errors.submit}
                                        <Button
                                            color='inherit'
                                            onClick={handleReRequestCode}
                                            style={{ marginLeft: '8px' }}
                                        >
                                            Begär ny kod
                                        </Button>
                                    </Alert>
                                )}

                                <TextField
                                    type='email'
                                    name='email'
                                    label='Epostadress'
                                    value={values.email}
                                    error={Boolean(touched.email && errors.email)}
                                    helperText={touched.email && errors.email}
                                    fullWidth
                                    onBlur={handleBlur}
                                    onChange={handleChange}
                                    margin='normal'
                                    inputRef={emailRef}
                                />

                                <TextField
                                    type='text'
                                    name='code'
                                    label='Återställningskod'
                                    value={values.code}
                                    error={Boolean(touched.code && errors.code)}
                                    helperText={touched.code && errors.code}
                                    fullWidth
                                    onBlur={handleBlur}
                                    onChange={handleChange}
                                    margin='normal'
                                    inputRef={codeRef}
                                />

                                <TextField
                                    type='password'
                                    name='newPassword'
                                    label='Nytt lösenord'
                                    value={values.newPassword}
                                    error={Boolean(touched.newPassword && errors.newPassword)}
                                    helperText={touched.newPassword && errors.newPassword}
                                    fullWidth
                                    onBlur={handleBlur}
                                    onChange={handleChange}
                                    margin='normal'
                                    inputRef={newPasswordRef}
                                />

                                <TextField
                                    type='password'
                                    name='confirmPassword'
                                    label='Bekräfta nytt lösenord'
                                    value={values.confirmPassword}
                                    error={Boolean(touched.confirmPassword && errors.confirmPassword)}
                                    helperText={touched.confirmPassword && errors.confirmPassword}
                                    fullWidth
                                    onBlur={handleBlur}
                                    onChange={handleChange}
                                    margin='normal'
                                    inputRef={confirmPasswordRef}
                                />

                                <Button
                                    type='submit'
                                    fullWidth
                                    variant='contained'
                                    color='primary'
                                    disabled={isSubmitting}
                                    style={{ marginTop: '16px' }}
                                >
                                    Återställ lösenord
                                </Button>
                            </form>
                        )}
                    </Formik>
                ) : (
                    <Alert severity='success'>
                        Lösenordet har återställts. Du kan nu{' '}
                        <Button color='inherit' onClick={() => navigate('/auth/sign-in')}>
                            logga in
                        </Button>
                        .
                    </Alert>
                )}
            </Wrapper>
        </React.Fragment>
    )
}

export default ResetPasswordWithCode
